import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { switchMap, map, catchError, of, withLatestFrom } from "rxjs";
import { CatalogClient } from "src/api-service";
import { selectSelectedCompany } from "../../../../user/user.selectors";
import { normalizeError } from "../../../../app-store-error.types";
import { CatalogManagementActions } from "./catalog-management.actions";

// **************************************************************************
// CATALOG MANAGEMENT EFFECTS
// Admin CRUD operations for catalogs
// Note: Load effect is in entities/catalog/catalogs
// **************************************************************************
@Injectable()
export class CatalogManagementEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly catalogClient = inject(CatalogClient);

    createCatalog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogManagementActions.createCatalog),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ request }, selectedCompany]) =>
                this.catalogClient.create(selectedCompany?.id ?? '', request).pipe(
                    map(catalog =>
                        CatalogManagementActions.createCatalogSuccess({
                            catalog,
                            toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogManagementActions.createCatalogFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteCatalog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogManagementActions.deleteCatalog),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id }, selectedCompany]) =>
                this.catalogClient.delete(String(selectedCompany.id), id).pipe(
                    map(() =>
                        CatalogManagementActions.deleteCatalogSuccess({
                            id,
                            toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogManagementActions.deleteCatalogFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    uploadCatalogImage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogManagementActions.uploadCatalogImage),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ catalogId, file }, selectedCompany]) =>
                this.catalogClient.uploadImage(
                    selectedCompany?.id ?? '',
                    catalogId,
                    { data: file, fileName: file.name },
                ).pipe(
                    map(catalog =>
                        CatalogManagementActions.uploadCatalogImageSuccess({
                            catalog,
                            toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.IMAGE_UPLOAD_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogManagementActions.uploadCatalogImageFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.CATALOGS.TOAST.IMAGE_UPLOAD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
